/**
 * Search Analytics Events
 *
 * Tracking functions for search-related analytics events.
 *
 * Event:
 * - no_result_preview: Fired when filter preview shows 0 results AND user applies the filters
 */

import { get, writable } from 'svelte/store';

import { pushToDataLayer } from '../core/dispatcher';
import type { SearchNoResultPreviewPayload } from '../core/types';

// =============================================================================
// Constants
// =============================================================================

const EVENT_NO_RESULT_PREVIEWED = 'a.search.no_result_previewed' as const;

// =============================================================================
// State Management
// =============================================================================

const filtersApplied = writable(false);

export function markFiltersApplied(): void {
    filtersApplied.set(true);
}

// =============================================================================
// Type Guard (internal)
// =============================================================================

function isSearchNoResultPreviewPayload(
    data: unknown,
): data is SearchNoResultPreviewPayload {
    return (
        typeof data === 'object' &&
        data !== null &&
        'event' in data &&
        (data as { event: string }).event === EVENT_NO_RESULT_PREVIEWED &&
        'eventData' in data
    );
}

// =============================================================================
// Main Tracking Function
// =============================================================================

export function handleSearchCountResult(
    count: number,
    analyticsPayload: unknown,
): void {
    const shouldTrack = get(filtersApplied);

    // Always clear flag after API response
    filtersApplied.set(false);

    if (
        shouldTrack &&
        count === 0 &&
        isSearchNoResultPreviewPayload(analyticsPayload)
    ) {
        pushToDataLayer(analyticsPayload);
    }
}
